import { Alert, Box, LinearProgress, Typography } from "@mui/material";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import { getBulkWorkTableColumns } from "../../pages/worker/constants";
import { HumanResourceService, ProjectService } from "../../services";
import { ActionButton, CardLayout, CrudTable, InputField, SelectField } from "../v3";
import { useHasPermission } from "../../utils";
import { ENABLE_WORK_BACKDATE_ENTRY } from "../../constants";

const getWorkerFromWork = (work, allWorkersMap = {}) => {
  if (work?.worker?.id) return work.worker;
  return allWorkersMap[String(work?.workerId)] || null;
};

const formatWorkRows = (works = [], allWorkersMap = {}) => {
  return works
    .map((work) => {
      if (work.isGroupWork) return null;
      const worker = getWorkerFromWork(work, allWorkersMap);
      if (!worker) return null;

      const overtimeHourlyRate = Number(worker?.overtimeHourlyRate ?? work?.overtimeRate ?? 0);
      const wage = Number(work?.wage ?? 0);
      const overtimeHours = Number(work?.overtimeHours ?? 0);

      return {
        id: String(work.id),
        workId: work.id,
        workerId: worker.id,
        workerName: worker.name || "-",
        designation: worker.designation || "-",
        wage,
        overtimeHours,
        overtimeHourlyRate,
        originalWork: work,
        wageError: "",
        overtimeError: "",
      };
    })
    .filter(Boolean);
};

const validateOvertimeHours = (value) => {
  if (value === "" || value == null) return "";
  const num = Number(value);
  if (isNaN(num)) return "Invalid overtime value";
  if (num > 0 && (num * 10) % 5 !== 0) return "Overtime must be a multiple of 0.5";
  if (num > 0 && num < 0.5) return "Overtime must be greater/equal to 0.5";
  return "";
};

export const AddEditBulkWorkForm = () => {
  const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
  const [selectedProject, setSelectedProject] = useState(null);

  const workerCacheRef = useRef({});
  const [workers, setWorkers] = useState([]);

  const [searchLoading, setSearchLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const [errors, setErrors] = useState({});

  const enableBackDate = useHasPermission(ENABLE_WORK_BACKDATE_ENTRY);

  const fetchWorksForProjectAndDate = useCallback(async ({ projectId, selectedDate }) => {
    if (!projectId || !selectedDate) return;

    setSearchLoading(true);

    try {
      const fromDate = moment(selectedDate).startOf("day").toISOString();
      const toDate = moment(selectedDate).endOf("day").toISOString();

      const { data } = await HumanResourceService.getWorks({
        projectId,
        fromDate,
        toDate,
        page: 1,
        limit: 100,
      });

      const works = data?.rows || [];

      // Extract unique workerIds
      const workerIds = [...new Set(works.map((w) => w.workerId))];

      // Determine which workers are missing in cache
      const cache = workerCacheRef.current;
      const missingWorkerIds = workerIds.filter((id) => !cache[id]);

      if (missingWorkerIds.length) {
        const { data: workerData } = await HumanResourceService.getEmployees({
          ids: missingWorkerIds.join(","),
        });

        const fetchedWorkers = workerData?.rows || [];

        fetchedWorkers.forEach((worker) => {
          cache[String(worker.id)] = worker;
        });
      }

      const formattedRows = formatWorkRows(works, cache);

      setWorkers(formattedRows);
    } catch (error) {
      console.error("Error fetching works:", error?.response?.data || error);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    const projectId = selectedProject?.id;
    if (!projectId || !date) {
      setWorkers([]);
      return;
    }

    setErrors({});
    setWorkers([]);
    fetchWorksForProjectAndDate({
      projectId,
      selectedDate: date,
    });
  }, [date, selectedProject, fetchWorksForProjectAndDate]);

  const validateRows = () => {
    let hasErrors = false;

    const validatedRows = workers.map((row) => {
      let wageError = "";
      let overtimeError = "";

      if (row.wage === "" || isNaN(Number(row.wage)) || Number(row.wage) <= 0) {
        wageError = "Wage is required";
        hasErrors = true;
      }

      if (row.overtimeHourlyRate <= 0 && Number(row.overtimeHours || 0) > 0) {
        overtimeError = "No overtime rate for this worker";
        hasErrors = true;
      } else {
        overtimeError = validateOvertimeHours(row.overtimeHours);
        if (overtimeError) hasErrors = true;
      }

      return {
        ...row,
        wageError,
        overtimeError,
      };
    });

    setWorkers(validatedRows);
    return !hasErrors;
  };

  const handleSubmit = async () => {
    if (loading) return;

    const nextErrors = {};
    if (!date) nextErrors.date = "Date is required";
    if (!selectedProject?.id) nextErrors.projectId = "Project is required";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    if (!workers.length) {
      toast.error("No workers to update");
      return;
    }

    if (!validateRows()) return;

    try {
      setLoading(true);
      const updates = workers.map((worker) => {
        const overtimeHours = Number(worker.overtimeHours) || 0;
        const overtimeRate = Number(worker.overtimeHourlyRate) || 0;
        const hasOvertime = overtimeHours > 0 && overtimeRate > 0;

        return {
          id: worker.workId,
          data: {
            wage: Number(worker.wage),
            date: worker.originalWork?.date,
            projectId: worker.originalWork?.projectId,
            description: worker.originalWork?.description,
            overtimeHours,
            ...(hasOvertime ? { overtimeRate } : {}),
          },
        };
      });

      await HumanResourceService.updateBulkWork({ updates });

      toast.success(`Added overtime for ${workers.length} workers successfully`);
      setSelectedProject(null);
      setWorkers([]);
    } catch (error) {
      const errorMessage = error?.response?.data?.message || "Failed to update workers";
      console.error("Bulk update failed:", error?.response?.data || error);
      toast.error(Array.isArray(errorMessage) ? errorMessage.join(", ") : errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const summary = useMemo(() => {
    return workers.reduce(
      (acc, row) => {
        const wage = Number(row.wage) || 0;
        const overtimeHours = Number(row.overtimeHours) || 0;
        const overtime = overtimeHours * (Number(row.overtimeHourlyRate) || 0);
        return {
          wageTotal: acc.wageTotal + wage,
          overtimeTotal: acc.overtimeTotal + overtime,
          grandTotal: acc.grandTotal + wage + overtime,
        };
      },
      { wageTotal: 0, overtimeTotal: 0, grandTotal: 0 },
    );
  }, [workers]);

  return (
    <>
      <CardLayout>
        <Box display="flex" flexDirection={{ xs: "column", md: "row" }} gap={2}>
          <InputField
            required
            label="Date"
            name="date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            error={!!errors.date}
            helperText={errors.date}
            fullWidth
            inputSlotProps={{
              htmlInput: {
                min: enableBackDate ? null : moment().format("YYYY-MM-DD"),
                onKeyDown: enableBackDate ? undefined : (e: any) => e.preventDefault(),
              },
            }}
          />
          <SelectField
            required
            label="Project"
            value={selectedProject}
            getData={ProjectService.getProjectList}
            getDataStaticFilters={{ status: ["INPROGRESS"], sortBy: "name", sortOrder: "asc" }}
            error={!!errors.projectId}
            helperText={errors.projectId}
            onChange={setSelectedProject}
            style={{ width: "100%" }}
          />
        </Box>

        {searchLoading && (
          <Box mt={1}>
            <LinearProgress />
          </Box>
        )}

        {!searchLoading && selectedProject?.id && workers.length === 0 && (
          <Alert sx={{ mt: 1 }} severity="info">
            No previous work found for this date and project.
          </Alert>
        )}
      </CardLayout>

      {workers.length > 0 && (
        <CardLayout>
          <CrudTable
            rows={workers}
            setRows={setWorkers}
            columns={getBulkWorkTableColumns({ validateOvertimeHours })}
            hidePagination
          />

          <Box
            mt={2}
            p={1.5}
            display="flex"
            flexDirection={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            sx={{ background: "#F7F7FC", borderRadius: "8px" }}
          >
            <Typography variant="body2">
              Wage Total: ₹ {summary.wageTotal.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
            </Typography>
            <Typography variant="body2">
              Overtime Total: ₹ {summary.overtimeTotal.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Grand Total: ₹ {summary.grandTotal.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
            </Typography>
          </Box>

          <Box mt={2} display="flex" justifyContent="flex-end">
            <ActionButton buttonText="Add Overtime" onButtonClick={handleSubmit} loading={loading} disabled={!workers.length} />
          </Box>
        </CardLayout>
      )}
    </>
  );
};
